import type { GhostBody, GhostFace, GhostHand, GhostHead, GhostNeck, GhostSpec } from "./spec.js";
import { GHOST_CLOTH_COLORS } from "./spec.js";

/** Body colors from the shared bot palette (`resolvePersonaColorDef` in `@rakazo/core`). */
export interface GhostColors {
  light: string;
  dark: string;
  eyeColor: string;
}

/** Theme-dependent paint. Web passes CSS variables, native passes literals. */
export interface GhostPaint {
  rim: string;
  rimOpacity: string;
  outline: string;
  shadow: string;
  shadowOpacity: string;
  impulse: string;
  steam: string;
}

export const GHOST_CSS_PAINT: GhostPaint = {
  rim: "var(--ghost-rim)",
  rimOpacity: "var(--ghost-rim-op)",
  outline: "var(--ghost-outline)",
  shadow: "var(--ghost-shadow)",
  shadowOpacity: "var(--ghost-shadow-op)",
  impulse: "var(--ghost-impulse)",
  steam: "currentColor",
};

export const GHOST_PAINT: Record<"dark" | "light", GhostPaint> = {
  dark: {
    rim: "#C4B5FF",
    rimOpacity: "0.6",
    outline: "none",
    shadow: "#000000",
    shadowOpacity: "0.5",
    impulse: "#A7FF3F",
    steam: "#F8F6EC",
  },
  light: {
    rim: "#FFFFFF",
    rimOpacity: "0.8",
    outline: "#0A1024",
    shadow: "#0A1024",
    shadowOpacity: "0.16",
    impulse: "#5DBB12",
    steam: "#0A1024",
  },
};

/** Finishing detail only; the outfit is drawn at every tier. s: compact crop, no shadow; m: adds ground shadow and flutter; l: adds soft light. */
export type GhostTier = "s" | "m" | "l";

export function ghostTier(size: number): GhostTier {
  return size <= 32 ? "s" : size <= 80 ? "m" : "l";
}

export interface GhostRenderOptions {
  spec: GhostSpec;
  colors: GhostColors;
  size: number;
  /** Unique per rendered instance; scopes gradient, clip and mask ids. */
  uid: string;
  /** Drives the per-bot idle timing. */
  seed: number;
  tier?: GhostTier;
  /** web: CSS classes, SMIL flutter and blur filters. native: plain SVG for react-native-svg. */
  target: "web" | "native";
  paint: GhostPaint;
  reducedMotion?: boolean;
}

const INK = "#0A1024";
const CREAM = "#F8F6EC";
const LIME = "#A7FF3F";
const NAVY = "#1E2547";
const GOLD = "#F0CB57";
const GOLD_DK = "#A77F16";

export const GHOST_VIEWBOX = "0 0 200 200";
const SMALL_VIEWBOX = "14 8 172 172";

interface BodyShape {
  hw: number;
  T: number;
  B: number;
  hb: number;
  lobes: number;
  d: number;
  tail?: boolean;
}

const BODIES: Record<GhostBody, BodyShape> = {
  classic: { hw: 54, T: 44, B: 164, hb: 60, lobes: 3, d: 10 },
  chubby: { hw: 63, T: 52, B: 160, hb: 66, lobes: 4, d: 9 },
  tall: { hw: 46, T: 34, B: 170, hb: 54, lobes: 3, d: 10 },
  wisp: { hw: 54, T: 46, B: 156, hb: 56, lobes: 0, d: 10, tail: true },
  bell: { hw: 47, T: 44, B: 164, hb: 70, lobes: 5, d: 8 },
  bean: { hw: 57, T: 62, B: 160, hb: 57, lobes: 2, d: 6 },
};

const f = (n: number) => Math.round(n * 10) / 10;

export function mixHex(a: string, b: string, t: number): string {
  const parse = (hex: string) => {
    const full = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
    return [1, 3, 5].map((i) => Number.parseInt(full.slice(i, i + 2), 16) || 0);
  };
  const A = parse(a);
  const B = parse(b);
  return `#${A.map((v, i) =>
    Math.round(v + ((B[i] ?? 0) - v) * t)
      .toString(16)
      .padStart(2, "0"),
  ).join("")}`;
}

function geometry(body: GhostBody) {
  const b = BODIES[body];
  const cx = 100;
  const s = b.hw / 54;
  const yS = b.T + b.hw * 0.92;
  const eyeY = b.T + b.hw * 0.9;
  return {
    ...b,
    cx,
    s,
    yS,
    eyeY,
    eyeDX: b.hw * 0.36,
    eyeRX: b.hw * 0.145,
    eyeRY: b.hw * 0.2,
    neckY: eyeY + b.hw * 0.62,
    handY: yS + (b.B - yS) * 0.42,
    armY: yS + 16 * s,
  };
}
type Geometry = ReturnType<typeof geometry>;

function bodyPath(g: Geometry, phase: 0 | 1): string {
  const { cx, T, hw, hb, B, yS } = g;
  const k = 0.552;
  let d = `M${cx} ${T}`;
  d += `C${f(cx + hw * k)} ${T} ${f(cx + hw)} ${f(yS - (yS - T) * k)} ${f(cx + hw)} ${f(yS)}`;
  d += `C${f(cx + hw)} ${f(yS + (B - yS) * 0.45)} ${f(cx + hb)} ${f(B - (B - yS) * 0.25)} ${f(cx + hb)} ${B}`;
  if (g.tail) {
    const ty = phase ? 6 : 0;
    const tx = phase ? -3 : 0;
    d += `C${f(cx + hb)} ${B + 9} ${f(cx + hb + 15 + tx)} ${B + 11 + ty} ${f(cx + hb + 25 + tx)} ${B + 4 + ty}`;
    d += `C${f(cx + hb + 14)} ${B + 25 + ty / 2} ${f(cx + hb * 0.1)} ${B + 22} ${f(cx - hb * 0.2)} ${B + 12}`;
    d += `C${f(cx - hb * 0.6)} ${B + 6} ${f(cx - hb)} ${B + 12} ${f(cx - hb)} ${B}`;
  } else {
    const n = g.lobes;
    const w = (2 * hb) / n;
    const c = g.d * 0.5;
    for (let i = 0; i < n; i += 1) {
      const x0 = cx + hb - i * w;
      const x1 = x0 - w;
      const y1 = i === n - 1 ? B : B - c;
      const flip = (i % 2 === 0) !== Boolean(phase);
      const yc = B + 1.33 * g.d * (flip ? 1.22 : 0.8);
      d += `C${f(x0 + w * 0.03)} ${f(yc)} ${f(x1 - w * 0.03)} ${f(yc)} ${f(x1)} ${f(y1)}`;
    }
  }
  d += `C${f(cx - hb)} ${f(B - (B - yS) * 0.25)} ${f(cx - hw)} ${f(yS + (B - yS) * 0.45)} ${f(cx - hw)} ${f(yS)}`;
  d += `C${f(cx - hw)} ${f(yS - (yS - T) * k)} ${f(cx - hw * k)} ${T} ${cx} ${T}Z`;
  return d;
}

// Accessories are drawn in local units: dome radius 54, origin at the slot anchor.

function headItem(kind: GhostHead, c: string): string {
  const dk = mixHex(c, INK, 0.35);
  switch (kind) {
    case "cap":
      return `<path d="M-12 20C-30 15-62 15-77 25C-67 34-34 33-8 28Z" fill="${dk}"/><path d="M-47 25C-49-6-28-24 0-24C28-24 49-6 47 25C22 18-22 18-47 25Z" fill="${c}"/><path d="M-47 25C-49-6-28-24 0-24C-18-16-30 0-33 21Z" fill="#fff" opacity=".12"/><path d="M0-24C-5-8-7 6-7 19M0-24C5-8 7 6 7 19" stroke="${dk}" stroke-width="2" fill="none" opacity=".45"/><circle cx="0" cy="-23" r="4.5" fill="${LIME}"/>`;
    case "beanie":
      return `<path d="M-50 30C-52-10-28-30 0-30C28-30 52-10 50 30Z" fill="${c}"/><path d="M-30-18C-36-4-38 6-38 14M-15-26C-18-10-19 4-19 14M0-29V14M15-26C18-10 19 4 19 14M30-18C36-4 38 6 38 14" stroke="${dk}" stroke-width="2.4" fill="none" opacity=".35" stroke-linecap="round"/><path d="M-53 16C-20 9 20 9 53 16L54 34C20 27-20 27-54 34Z" fill="${dk}"/><circle cx="0" cy="-33" r="10" fill="${CREAM}"/><circle cx="3" cy="-30" r="5" fill="${INK}" opacity=".08"/>`;
    case "tophat":
      return `<g transform="translate(8 8) rotate(9)"><ellipse cx="0" cy="0" rx="36" ry="8" fill="${INK}"/><path d="M-22-2L-24-48C-24-52 24-52 24-48L22-2Z" fill="${INK}"/><path d="M-22.6-15H22.6L22.3-4H-22.3Z" fill="${c}"/><path d="M-16-45L-15-20" stroke="#fff" stroke-width="4" stroke-linecap="round" opacity=".16"/><ellipse cx="0" cy="-1" rx="33" ry="5" fill="none" stroke="#fff" stroke-width="1.2" opacity=".14"/></g>`;
    case "headset":
      return `<path d="M-55 40C-59-26 59-26 55 40" stroke="${INK}" stroke-width="7" fill="none" stroke-linecap="round"/><rect x="-65" y="30" width="17" height="31" rx="8.5" fill="${INK}"/><rect x="48" y="30" width="17" height="31" rx="8.5" fill="${INK}"/><rect x="-63" y="36" width="5" height="19" rx="2.5" fill="${LIME}"/><rect x="58" y="36" width="5" height="19" rx="2.5" fill="${LIME}"/><path d="M57 58C58 76 44 82 26 80" stroke="${INK}" stroke-width="4" fill="none" stroke-linecap="round"/><circle cx="24" cy="80" r="6" fill="${INK}"/>`;
    case "bow":
      return `<g transform="translate(30 13) rotate(24)"><path d="M0 0C-8-15-27-15-25 0C-27 15-8 15 0 0Z" fill="${c}"/><path d="M0 0C8-15 27-15 25 0C27 15 8 15 0 0Z" fill="${c}"/><path d="M-6-6C-14-10-20-6-20 0" stroke="#fff" stroke-width="2.5" fill="none" opacity=".3" stroke-linecap="round"/><circle r="5.5" fill="${dk}"/></g>`;
    case "sprout":
      return `<path d="M0 6C0-6 3-14 0-22" stroke="#4E8F2E" stroke-width="3.5" fill="none" stroke-linecap="round"/><path d="M0-19C-6-32-23-33-26-24C-19-15-6-15 0-19Z" fill="#8BD95A"/><path d="M0-22C6-37 23-38 27-29C19-18 6-18 0-22Z" fill="${LIME}"/>`;
    case "crown":
      return `<g transform="translate(4 4) rotate(-6)"><path d="M-25 8L-29-22L-13-8L0-29L13-8L29-22L25 8Z" fill="${GOLD}" stroke="${GOLD_DK}" stroke-width="2" stroke-linejoin="round"/><path d="M-25 8H25" stroke="${GOLD_DK}" stroke-width="2"/><circle cx="0" cy="-29" r="3.5" fill="${c}"/><circle cx="-29" cy="-22" r="3" fill="${c}"/><circle cx="29" cy="-22" r="3" fill="${c}"/><circle cx="0" cy="0" r="3.2" fill="${c}"/></g>`;
  }
}

/** Contact shadow under brimmed hats: [y offset, half width]. */
const HAT_SHADOW: Partial<Record<GhostHead, [number, number]>> = {
  cap: [25, 46],
  beanie: [32, 52],
  tophat: [12, 32],
};

function faceItem(kind: GhostFace): string {
  switch (kind) {
    case "round":
      return `<g stroke="${INK}" stroke-width="4" fill="none"><circle cx="-20" cy="0" r="15" fill="#fff" fill-opacity=".14"/><circle cx="20" cy="0" r="15" fill="#fff" fill-opacity=".14"/><path d="M-5-2C-2-6 2-6 5-2M-35-3L-49-7M35-3L49-7" stroke-linecap="round"/></g><path d="M-28-7A10 10 0 0 1-21-11M12-7A10 10 0 0 1 19-11" stroke="#fff" stroke-width="2.4" fill="none" stroke-linecap="round" opacity=".8"/>`;
    case "nerd":
      return `<g stroke="#3B2418" stroke-width="5.5" fill="none" stroke-linejoin="round"><rect x="-37" y="-13" width="31" height="25" rx="8" fill="#fff" fill-opacity=".12"/><rect x="6" y="-13" width="31" height="25" rx="8" fill="#fff" fill-opacity=".12"/><path d="M-6-4C-2-8 2-8 6-4M-37-6L-50-9M37-6L50-9" stroke-linecap="round"/></g>`;
    case "shades":
      return `<path d="M-40-11H40" stroke="${INK}" stroke-width="4.5" stroke-linecap="round"/><path d="M-39-11C-39-14-6-14-5-11C-4 4-10 14-22 14C-34 14-39 2-39-11Z" fill="${INK}"/><path d="M39-11C39-14 6-14 5-11C4 4 10 14 22 14C34 14 39 2 39-11Z" fill="${INK}"/><path d="M-31-6L-22-6M13-6L22-6" stroke="#fff" stroke-width="3" stroke-linecap="round" opacity=".55"/>`;
    case "monocle":
      return `<circle cx="20" cy="0" r="15" fill="#fff" fill-opacity=".14" stroke="${GOLD}" stroke-width="3.5"/><path d="M31 10C40 30 30 46 18 54" stroke="${GOLD}" stroke-width="1.8" stroke-dasharray="2 3" fill="none" stroke-linecap="round"/><path d="M11-7A10 10 0 0 1 18-11" stroke="#fff" stroke-width="2.4" fill="none" stroke-linecap="round" opacity=".8"/>`;
  }
}

function tie(c: string): string {
  return `<path d="M-6 0H6L4.5 9H-4.5Z" fill="${mixHex(c, INK, 0.3)}"/><path d="M-4.5 9H4.5L8 38L0 47L-8 38Z" fill="${c}"/><path d="M-4 16L5 12M-6 26L7 21M-7 36L7 31" stroke="#fff" stroke-width="2" opacity=".25"/>`;
}

/** [clipped to the body, drawn on top]. */
function neckItem(kind: GhostNeck, c: string): [string, string] {
  switch (kind) {
    case "tie":
      return ["", tie(c)];
    case "bowtie":
      return [
        "",
        `<path d="M0 3L-19-7C-22-4-22 12-19 15Z" fill="${c}"/><path d="M0 3L19-7C22-4 22 12 19 15Z" fill="${c}"/><rect x="-5" y="-2" width="10" height="10" rx="3" fill="${mixHex(c, INK, 0.3)}"/>`,
      ];
    case "suit":
      return [
        `<path d="M-300-4H-19L0 46L19-4H300V300H-300Z" fill="${NAVY}"/><path d="M-19-4L-6 30M19-4L6 30" stroke="#3A4580" stroke-width="3" fill="none"/><circle cx="0" cy="58" r="2.8" fill="${INK}"/><circle cx="0" cy="71" r="2.8" fill="${INK}"/><path d="M26 22L40 15L44 22Z" fill="${CREAM}"/>`,
        tie(c),
      ];
    case "scarf":
      return [
        `<path d="M-300-6C-30 5 30 5 300-6V13C30 23-30 23-300 13Z" fill="${c}"/><path d="M-300 4C-30 14 30 14 300 4" stroke="#fff" stroke-width="2" opacity=".25" fill="none"/>`,
        `<path d="M10 10C14 22 14 35 12 46H29C31 33 29 19 24 8Z" fill="${mixHex(c, INK, 0.12)}"/><path d="M13 32H29M13 38H29" stroke="${CREAM}" stroke-width="2.4" opacity=".7"/><path d="M14 46V51M19 46V51M24 46V51M28 46V51" stroke="${c}" stroke-width="2" stroke-linecap="round"/>`,
      ];
    case "lanyard":
      return [
        "",
        `<path d="M-26-8L-5 24M26-8L5 24" stroke="${c}" stroke-width="3.2" stroke-linecap="round" fill="none"/><rect x="-14" y="23" width="28" height="34" rx="5" fill="${CREAM}" stroke="${INK}" stroke-opacity=".18"/><rect x="-14" y="23" width="28" height="7" rx="3" fill="${LIME}"/><circle cx="-4" cy="39" r="2.4" fill="${INK}"/><circle cx="4" cy="39" r="2.4" fill="${INK}"/><path d="M-8 47H8M-6 51H6" stroke="${INK}" stroke-width="1.6" opacity=".35" stroke-linecap="round"/>`,
      ];
  }
}

interface ArmPose {
  x: number;
  y: number;
  rot: number;
}

function handItem(
  kind: GhostHand,
  steam: string,
): { at: [number, number]; rot?: number; svg: string; arms: [ArmPose | null, ArmPose | null] } {
  switch (kind) {
    case "coffee":
      return {
        at: [30, 0],
        svg: `<path d="M-11-12H11L8 18H-8Z" fill="${INK}"/><rect x="-13.5" y="-19" width="27" height="8" rx="3" fill="#2A3150"/><path d="M-4 0C-4-5 4-5 4 0V7L2.6 5.6 1.3 7 0 5.6-1.3 7-2.6 5.6-4 7Z" fill="${CREAM}"/><path d="M-4-24C-6-28-2-30-4-34M4-24C2-28 6-30 4-34" ${paint({ stroke: steam })} stroke-width="1.8" fill="none" opacity=".35" stroke-linecap="round"/>`,
        arms: [null, { x: 44, y: 4, rot: -62 }],
      };
    case "clipboard":
      return {
        at: [-6, 6],
        rot: -8,
        svg: `<rect x="-19" y="-25" width="38" height="48" rx="5" fill="#2A2F52"/><rect x="-14" y="-18" width="28" height="36" rx="2" fill="${CREAM}"/><rect x="-8" y="-28" width="16" height="7" rx="2.5" fill="#8A90A8"/><path d="M-10-9L-8-6.5-4-11M-10 1L-8 3.5-4-1M-10 11L-8 13.5-4 9" stroke="#3E9B12" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="M0-8.5H9M0 1.5H9M0 11.5H6" stroke="${INK}" stroke-width="2" fill="none" opacity=".35" stroke-linecap="round"/>`,
        arms: [
          { x: -28, y: 4, rot: 62 },
          { x: 16, y: 10, rot: -62 },
        ],
      };
    case "tablet":
      return {
        at: [6, 6],
        rot: 8,
        svg: `<rect x="-23" y="-17" width="46" height="35" rx="6" fill="${INK}"/><rect x="-19" y="-13" width="38" height="27" rx="3" fill="#27306A"/><rect x="-14" y="-8" width="18" height="4" rx="2" fill="${LIME}"/><path d="M-14 2H12M-14 8H6" stroke="#fff" stroke-width="2.2" opacity=".35" stroke-linecap="round"/>`,
        arms: [
          { x: -24, y: 8, rot: 66 },
          { x: 34, y: 6, rot: -66 },
        ],
      };
    case "laptop":
      return {
        at: [0, 16],
        svg: `<rect x="-44" y="-26" width="88" height="52" rx="7" fill="#1B2240"/><rect x="-44" y="-26" width="88" height="52" rx="7" fill="none" stroke="#fff" stroke-opacity=".08"/><path d="M-7 4C-7-6 7-6 7 4V11L4.7 8.8 2.3 11 0 8.8-2.3 11-4.7 8.8-7 11Z" fill="${CREAM}" opacity=".85"/><rect x="-50" y="25" width="100" height="7" rx="3.5" fill="#2A3150"/>`,
        arms: [
          { x: -40, y: -26, rot: 72 },
          { x: 40, y: -26, rot: -72 },
        ],
      };
  }
}

/** CSS variables only work in a style attribute; literals stay attributes for react-native-svg. */
function paint(props: Record<string, string>): string {
  const attrs: string[] = [];
  const styles: string[] = [];
  for (const [key, value] of Object.entries(props)) {
    if (value.startsWith("var(")) styles.push(`${key}:${value}`);
    else attrs.push(`${key}="${value}"`);
  }
  if (styles.length) attrs.push(`style="${styles.join(";")}"`);
  return attrs.join(" ");
}

export interface GhostParts {
  viewBox: string;
  defs: string;
  /** Ground shadow, outside the floating group. */
  shadow: string;
  /** Body, clipped clothing and shading. */
  body: string;
  eyes: string;
  happyEyes: string;
  mouth: string;
  /** Glasses, neckwear, hat, held item and arms. */
  over: string;
  impulse: string;
  /** Eye line in viewBox units, for blink transforms. */
  eyeY: number;
  groundY: number;
}

export function ghostParts(options: GhostRenderOptions): GhostParts {
  const { spec, colors, target } = options;
  const web = target === "web";
  const tier = options.tier ?? ghostTier(options.size);
  const u = options.uid.replace(/[^a-zA-Z0-9_-]/g, "");
  const g = geometry(spec.body);
  const { cx, s, eyeY, eyeDX, eyeRX, eyeRY } = g;
  const p = options.paint;
  const cloth = GHOST_CLOTH_COLORS[spec.cloth] ?? GHOST_CLOTH_COLORS[0];
  const lightEyes = colors.eyeColor.toLowerCase() === "#ffffff";
  const hi = mixHex(colors.light, "#ffffff", 0.5);
  const shade = mixHex(colors.dark, "#241B4A", 0.35);
  const blur = web && tier === "l";
  const fd = 4.2 + (options.seed % 24) / 10;
  const flutter =
    web && !options.reducedMotion && tier !== "s"
      ? `<animate attributeName="d" values="${bodyPath(g, 0)};${bodyPath(g, 1)};${bodyPath(g, 0)}" dur="${f(fd * 0.8)}s" repeatCount="indefinite" calcMode="spline" keySplines=".45 0 .55 1;.45 0 .55 1"/>`
      : "";
  const shapeEl = (attrs: string) => `<path d="${bodyPath(g, 0)}" ${attrs}>${flutter}</path>`;

  let neck = spec.neck;
  if ((neck === "tie" || neck === "suit") && g.B - g.neckY < 30 * s) neck = "bowtie";
  const hand = spec.hand ? handItem(spec.hand, p.steam) : null;
  const [neckClip, neckTop] = neck ? neckItem(neck, cloth) : ["", ""];
  const armFill = neck === "suit" ? NAVY : `url(#${u}-fill)`;

  const defs = `<defs><radialGradient id="${u}-fill" cx=".38" cy=".3" r=".82" fx=".33" fy=".22"><stop offset="0" stop-color="${hi}"/><stop offset=".55" stop-color="${colors.light}"/><stop offset="1" stop-color="${colors.dark}"/></radialGradient><linearGradient id="${u}-shade" x1="0" y1="0" x2="0" y2="1"><stop offset=".42" stop-color="${shade}" stop-opacity="0"/><stop offset="1" stop-color="${shade}" stop-opacity=".7"/></linearGradient><clipPath id="${u}-clip">${shapeEl("")}</clipPath><mask id="${u}-rim" maskUnits="userSpaceOnUse" x="-20" y="-20" width="240" height="240">${shapeEl('fill="#fff"')}${shapeEl('fill="#000" transform="translate(-7 -6)"')}</mask>${
    blur
      ? `<filter id="${u}-soft" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="2.2"/></filter><filter id="${u}-gs" x="-50%" y="-200%" width="200%" height="500%"><feGaussianBlur stdDeviation="4"/></filter>`
      : ""
  }</defs>`;

  const groundY = g.B + (g.tail ? 30 : g.d * 1.2 + 14);
  const shadow =
    tier === "s"
      ? ""
      : `<ellipse ${web ? 'class="g-shadow"' : ""} cx="${cx}" cy="${f(groundY)}" rx="${f(g.hb * 0.78)}" ry="${f(6.5 * s)}" ${blur ? `filter="url(#${u}-gs)"` : ""} ${paint({ fill: p.shadow, opacity: p.shadowOpacity })}/>`;

  const hatShadow = spec.head && tier !== "s" ? HAT_SHADOW[spec.head] : undefined;
  const hlx = cx - g.hw * 0.42;
  const hly = g.T + g.hw * 0.36;
  const body = [
    shapeEl(`fill="url(#${u}-fill)"`),
    neckClip
      ? `<g clip-path="url(#${u}-clip)"><g transform="translate(${cx} ${f(g.neckY)}) scale(${f(s)})">${neckClip}</g></g>`
      : "",
    hatShadow
      ? `<g clip-path="url(#${u}-clip)"><ellipse cx="${cx}" cy="${f(g.T + hatShadow[0] * s)}" rx="${f(hatShadow[1] * s)}" ry="${f(7 * s)}" fill="${INK}" opacity=".2"/></g>`
      : "",
    shapeEl(`fill="url(#${u}-shade)"`),
    `<g ${blur ? `filter="url(#${u}-soft)"` : ""}><rect x="-20" y="-20" width="240" height="240" mask="url(#${u}-rim)" ${paint({ fill: p.rim, opacity: p.rimOpacity })}/></g>`,
    `<ellipse cx="${f(hlx)}" cy="${f(hly)}" rx="${f(g.hw * 0.2)}" ry="${f(g.hw * 0.1)}" transform="rotate(-38 ${f(hlx)} ${f(hly)})" fill="#fff" opacity="${blur ? 0.75 : 0.55}" ${blur ? `filter="url(#${u}-soft)"` : ""}/>`,
    p.outline === "none"
      ? ""
      : shapeEl(
          `fill="none" stroke-width="1.2" vector-effect="non-scaling-stroke" ${paint({ stroke: p.outline, "stroke-opacity": p.outline.startsWith("var(") ? "1" : "0.16" })}`,
        ),
    spec.cheeks
      ? [-1, 1]
          .map(
            (side) =>
              `<ellipse cx="${f(cx + side * (eyeDX + eyeRX * 0.9))}" cy="${f(eyeY + eyeRY + 4 * s)}" rx="${f(5.5 * s)}" ry="${f(3.4 * s)}" fill="#FF7A9A" opacity=".32"/>`,
          )
          .join("")
      : "",
  ].join("");

  const eyeClass = web ? 'class="g-eye"' : "";
  const eye = (x: number) =>
    lightEyes
      ? `<g ${eyeClass}><ellipse cx="${f(x)}" cy="${f(eyeY)}" rx="${f(eyeRX * 1.18)}" ry="${f(eyeRY * 1.08)}" fill="#fff"/><ellipse cx="${f(x + eyeRX * 0.12)}" cy="${f(eyeY + eyeRY * 0.12)}" rx="${f(eyeRX * 0.72)}" ry="${f(eyeRY * 0.72)}" fill="${INK}"/><circle cx="${f(x + eyeRX * 0.38)}" cy="${f(eyeY - eyeRY * 0.22)}" r="${f(eyeRX * 0.26)}" fill="#fff"/></g>`
      : `<g ${eyeClass}><ellipse cx="${f(x)}" cy="${f(eyeY)}" rx="${f(eyeRX)}" ry="${f(eyeRY)}" fill="${INK}"/><circle cx="${f(x + eyeRX * 0.32)}" cy="${f(eyeY - eyeRY * 0.42)}" r="${f(eyeRX * 0.36)}" fill="#fff"/></g>`;
  const eyes = eye(cx - eyeDX) + eye(cx + eyeDX);
  const happyEyes = [-1, 1]
    .map((side) => {
      const x = cx + side * eyeDX;
      return `<path d="M${f(x - eyeRX)} ${f(eyeY + 3)}Q${f(x)} ${f(eyeY - eyeRY * 1.1)} ${f(x + eyeRX)} ${f(eyeY + 3)}" stroke="${lightEyes ? "#fff" : INK}" stroke-width="${f(5 * s)}" fill="none" stroke-linecap="round"/>`;
    })
    .join("");
  const my = eyeY + eyeRY + 9 * s;
  const mouth =
    spec.mouth === "none"
      ? ""
      : spec.mouth === "smile"
        ? `<path d="M${f(cx - 6 * s)} ${f(my)}Q${cx} ${f(my + 6 * s)} ${f(cx + 6 * s)} ${f(my)}" stroke="${INK}" stroke-width="${f(3.4 * s)}" fill="none" stroke-linecap="round"/>`
        : `<path d="M${f(cx - 8 * s)} ${f(my - 1)}Q${cx} ${f(my + 12 * s)} ${f(cx + 8 * s)} ${f(my - 1)}Z" fill="${INK}"/>`;

  const rx = 9 * s;
  const ry = 14 * s;
  const defaults: ArmPose[] = [
    { x: cx - g.hw - 1, y: g.armY, rot: 30 },
    { x: cx + g.hw + 1, y: g.armY, rot: -30 },
  ];
  const arms = defaults
    .map((fallback, index) => {
      const held = hand?.arms[index];
      const pose = held ? { x: cx + held.x * s, y: g.handY + held.y * s, rot: held.rot } : fallback;
      const el = `<ellipse cx="${f(pose.x)}" cy="${f(pose.y)}" rx="${f(rx)}" ry="${f(ry)}" transform="rotate(${pose.rot} ${f(pose.x)} ${f(pose.y)})" fill="${armFill}"/>`;
      return web && index === 1 && !held
        ? `<g class="g-arm-wave" style="transform-origin:${f(pose.x - 4 * s)}px ${f(pose.y - ry * 0.7)}px">${el}</g>`
        : el;
    })
    .join("");

  const over = [
    spec.face
      ? `<g transform="translate(${cx} ${f(eyeY)}) scale(${f(s)})">${faceItem(spec.face)}</g>`
      : "",
    neckTop ? `<g transform="translate(${cx} ${f(g.neckY)}) scale(${f(s)})">${neckTop}</g>` : "",
    spec.head
      ? `<g transform="translate(${cx} ${g.T}) scale(${f(s)})">${headItem(spec.head, cloth)}</g>`
      : "",
    hand
      ? `<g transform="translate(${f(cx + hand.at[0] * s)} ${f(g.handY + hand.at[1] * s)}) rotate(${hand.rot ?? 0}) scale(${f(s)})">${hand.svg}</g>`
      : "",
    arms,
  ].join("");

  const dome = g.T + g.hw;
  const impulse = [44, 68]
    .map((angle) => {
      const rad = (angle * Math.PI) / 180;
      const r1 = g.hw + 18 * s;
      const r2 = g.hw + 33 * s;
      return `<line x1="${f(cx + r1 * Math.sin(rad))}" y1="${f(dome - r1 * Math.cos(rad))}" x2="${f(cx + r2 * Math.sin(rad))}" y2="${f(dome - r2 * Math.cos(rad))}" stroke-width="${f(6 * s)}" stroke-linecap="round" ${paint({ stroke: p.impulse })}/>`;
    })
    .join("");

  return {
    viewBox: tier === "s" ? SMALL_VIEWBOX : GHOST_VIEWBOX,
    defs,
    shadow,
    body,
    eyes,
    happyEyes,
    mouth,
    over,
    impulse,
    eyeY,
    groundY,
  };
}

/** One animated SVG for the web; motion and theme come from `ghost-avatar.css`. */
export function ghostSvg(options: Omit<GhostRenderOptions, "target" | "paint">): string {
  const parts = ghostParts({ ...options, target: "web", paint: GHOST_CSS_PAINT });
  const tier = options.tier ?? ghostTier(options.size);
  const seed = options.seed;
  const timing = `--fd:${f(4.2 + (seed % 24) / 10)}s;--dl:${f(-((seed % 45) / 10))}s;--bd:${f(4.4 + (seed % 30) / 10)}s;--look:g-look-${seed % 4};--glow:${options.colors.light}`;
  // The size is inline so a parent's bare-SVG icon rule (buttons size every svg without a
  // `size-*` class) cannot shrink the ghost into the top-left of its frame.
  const frame = `width:${options.size}px;height:${options.size}px`;
  return `<svg class="ghost" data-tier="${tier}" viewBox="${parts.viewBox}" width="${options.size}" height="${options.size}" aria-hidden="true" focusable="false" style="${timing};${frame}">${parts.defs}${parts.shadow}<g class="g-float">${parts.body}<g class="g-look"><g class="g-eyes-open">${parts.eyes}</g><g class="g-eyes-happy">${parts.happyEyes}</g>${parts.mouth}</g>${parts.over}<g class="g-impulse">${parts.impulse}</g></g></svg>`;
}

/** Wraps one layer as a standalone document for react-native-svg's `SvgXml`. */
export function ghostLayerXml(parts: GhostParts, layer: string): string {
  return `<svg viewBox="${parts.viewBox}">${parts.defs}${layer}</svg>`;
}
